import React, { useState, useMemo } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './MarkdownEditableField.css';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import { PencilIcon, XMarkIcon, ClipboardDocumentIcon, CheckIcon } from '@heroicons/react/24/outline';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import TurndownService from 'turndown';
import { Converter } from 'showdown';
import { appUtils } from '../../utils/AppUtils';

// Configurar convertidores
const turndownService = new TurndownService({
  headingStyle: 'atx',
  codeBlockStyle: 'fenced'
});

const showdownConverter = new Converter({
  tables: true,
  strikethrough: true,
  tasklists: true
});

function MarkdownEditableField({ 
  value, 
  onSave, 
  minHeight = 200,
  placeholder,
  className = '',
  showEditButton = true,
  showCopyButton = true,
  modalTitle
}) {
  const { t } = useTranslation();
  const defaultPlaceholder = placeholder || t('common.enterText');
  const defaultModalTitle = modalTitle || t('common.edit');
  const [isEditing, setIsEditing] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [htmlContent, setHtmlContent] = useState('');

  // Configuración del toolbar de Quill - simplificado
  const modules = useMemo(() => ({
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['blockquote', 'link']
    ]
  }), []);

  const formats = [
    'bold', 'italic', 'underline',
    'list', 'bullet',
    'blockquote',
    'link'
  ];

  const handleEdit = (e) => {
    e.stopPropagation();
    // Convertir markdown a HTML para editar
    const html = value ? showdownConverter.makeHtml(value) : '';
    setHtmlContent(html);
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      // Convertir HTML de vuelta a markdown
      const markdown = turndownService.turndown(htmlContent);
      const trimmedValue = markdown.trim();
      
      if (trimmedValue !== value) {
        await onSave(trimmedValue);
      }
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving:', error);
      // Keep editing mode on error
    }
  };

  const handleCancel = () => {
    setHtmlContent('');
    setIsEditing(false);
  };

  const handleCopy = (e) => {
    e.stopPropagation();
    if (!value) return;
    
    const plainText = appUtils.markdownToPlainText(value);

    navigator.clipboard.writeText(plainText).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    }).catch(err => {
      console.error('Failed to copy text: ', err);
    });
  };

  // Modal de edición
  const renderEditModal = () => {
    if (!isEditing) return null;

    return (
      <div className="fixed inset-0 z-[100] overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
        {/* Background backdrop */}
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCancel}></div>

        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl flex flex-col max-h-[90vh]">
            
            {/* Header */}
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
              <h3 className="text-lg font-semibold leading-6 text-gray-900" id="modal-title">
                {defaultModalTitle}
              </h3>
              <button
                onClick={handleCancel}
                className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>

            {/* Body */}
            <div className="bg-white px-4 py-4 sm:p-6 overflow-y-auto flex-grow">
              <div className="quill-wrapper h-full">
                <ReactQuill
                  theme="snow"
                  value={htmlContent}
                  onChange={setHtmlContent}
                  modules={modules}
                  formats={formats}
                  placeholder={defaultPlaceholder}
                  className="h-full flex flex-col"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 flex-shrink-0 border-t border-gray-200">
              <button
                type="button"
                className="inline-flex w-full justify-center rounded-md bg-orange-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-600 sm:ml-3 sm:w-auto"
                onClick={handleSave}
              >
                {t('common.save')}
              </button>
              <button
                type="button"
                className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                onClick={handleCancel}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className={`markdown-display-container relative group ${className}`}>
        <div className="absolute -top-9 right-0 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
          {showCopyButton && value && (
            <button
              onClick={handleCopy}
              className="p-1.5 bg-white rounded-md shadow-sm hover:bg-gray-50 border border-gray-200"
              title="Copy to clipboard"
            >
              {isCopied ? (
                <CheckIcon className="w-4 h-4 text-green-600" strokeWidth={1.5} />
              ) : (
                <ClipboardDocumentIcon className="w-4 h-4 text-gray-600" strokeWidth={1.5} />
              )}
            </button>
          )}
          {showEditButton && (
            <button
              onClick={handleEdit}
              className="p-1.5 bg-white rounded-md shadow-sm hover:bg-gray-50 border border-gray-200"
              title={t('common.edit')}
            >
              <PencilIcon className="w-4 h-4 text-gray-600" strokeWidth={2.5} />
            </button>
          )}
        </div>
        <div className="prose max-w-none">
          {value ? (
            <ReactMarkdown rehypePlugins={[rehypeSanitize]}>{value}</ReactMarkdown>
          ) : (
            <p className="text-gray-400 italic cursor-pointer" onClick={handleEdit}>{defaultPlaceholder}</p>
          )}
        </div>
      </div>
      {renderEditModal()}
    </>
  );
}

MarkdownEditableField.propTypes = {
  value: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  minHeight: PropTypes.number,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  showEditButton: PropTypes.bool,
  showCopyButton: PropTypes.bool,
  modalTitle: PropTypes.string
};

export default MarkdownEditableField;
